import compact from 'lodash-es/compact'
import { ReactElement, cloneElement, useMemo } from 'react'

import { LoadingSpinner, TypeStyle } from '@syconium/little-miss-figgy'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { reportClientError } from '../../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useNextQuery } from '../../../../../app/_hooks/useNextQuery'
import { usePreviewDirectives } from '../../../../../app/_providers/PreviewDirectivesProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'

import { ShopTheLookTile, ShopTheLookTileProps } from './ShopTheLookTile'
import { Body, LoadingSpinnerWrapper, TilesList } from './styles'

export type ShopTheLookSectionProps = {
  wrapper?: ReactElement
  handle: string
} & (
  | {
      isKit: true
    }
  | {
      colorKey: string
      isKit: false
    }
)

export function ShopTheLookSection(props: ShopTheLookSectionProps) {
  const { isKit, handle, wrapper } = props
  const colorKey = props.isKit ? '' : props.colorKey
  const { shopTheLook: shopTheLookStrings } = useTranslation()
  const { includeUnavailableProducts } = usePreviewDirectives()

  // TODO: Update catalog-service to support Kits and remove this alternative query.
  const {
    data: kitData,
    previousData: previousKitData,
    status: kitStatus,
  } = useNextQuery(
    gql(`
      query GetKitShopTheLookAssociations($productHandle: String!) {
        kit(handle: $productHandle) {
          shopTheLook {
            __typename
            id
            availableForSale
            colorInfo {
              rawName
            }
            rawFit
            productGroup {
              __typename
              id
              handle
            }
          }
        }
      }
    `),
    {
      onError: error => {
        reportClientError({
          error,
          context: {
            scope: 'Shop the Look Section',
          },
        })
      },
      variables: { productHandle: handle },
      skip: !isKit,
    }
  )

  const {
    data: standardProductData,
    previousData: previousStandardProductData,
    status: standardProductStatus,
  } = useNextQuery(
    gql(`
      query GetShopTheLookAssociations($productGroupHandle: String!, $variantGroupColor: String!) {
        productCatalogStandardProduct(handle: $productGroupHandle) {
          variantGroup(color: $variantGroupColor) {
            __typename
            id
            associations {
              associationType
              variantGroup {
                __typename
                id
                availability
                associatedExclusiveCollectionHandle
                color {
                  id
                }
                variant {
                  __typename
                  id
                  fit {
                    id
                  }
                }
                product {
                  __typename
                  id
                  handle
                }
              }
            }
          }
        }
      }
    `),
    {
      onError: error => {
        reportClientError({
          error,
          context: {
            scope: 'Shop the Look Section',
          },
        })
      },
      variables: { productGroupHandle: handle, variantGroupColor: colorKey },
      skip: isKit,
    }
  )

  const data = useMemo<ShopTheLookTileProps[]>(() => {
    if (isKit) {
      const data = kitData ?? previousKitData
      return compact(
        (data?.kit?.shopTheLook ?? []).map(associatedProduct => {
          if (
            (includeUnavailableProducts || associatedProduct.availableForSale) &&
            associatedProduct.colorInfo?.rawName
          ) {
            return {
              productGroupHandle: associatedProduct.productGroup.handle,
              colorKey: associatedProduct.colorInfo?.rawName,
              fitKey: associatedProduct.rawFit,
              collectionHandle: undefined,
            }
          } else {
            return null
          }
        })
      )
    } else {
      const data = standardProductData ?? previousStandardProductData
      return compact(
        (data?.productCatalogStandardProduct.variantGroup.associations ?? []).map(association => {
          if (
            (includeUnavailableProducts ||
              association.variantGroup.availability !== 'UNAVAILABLE') &&
            association.associationType === 'SHOP_THE_LOOK' &&
            association.variantGroup.color
          ) {
            return {
              productGroupHandle: association.variantGroup.product.handle,
              colorKey: association.variantGroup.color.id,
              fitKey: association.variantGroup.variant?.fit?.id,
              collectionHandle: association.variantGroup.associatedExclusiveCollectionHandle,
            }
          } else {
            return null
          }
        })
      )
    }
  }, [
    includeUnavailableProducts,
    isKit,
    kitData,
    previousKitData,
    previousStandardProductData,
    standardProductData,
  ])
  const status = isKit ? kitStatus : standardProductStatus

  const headline = shopTheLookStrings.headline

  if (status === 'idle' || status === 'pending') {
    return (
      <LoadingSpinnerWrapper>
        <LoadingSpinner label='loading' size='48px' />
      </LoadingSpinnerWrapper>
    )
  }

  if (status === 'rejected' || data.length === 0) return null

  return cloneElement(wrapper ?? <></>, {
    children: (
      <Body>
        <TypeStyle.HeadlineSmall asTag='h2'>{headline}</TypeStyle.HeadlineSmall>

        <TilesList>
          {data.map((shopTheLookTileProps, index) => {
            return (
              <li
                key={`${shopTheLookTileProps.productGroupHandle}-${shopTheLookTileProps.colorKey}-${index}`}
              >
                <ShopTheLookTile {...shopTheLookTileProps} />
              </li>
            )
          })}
        </TilesList>
      </Body>
    ),
  })
}
